import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Championship, Car, ChampionshipRegistration } from '../types';
import { FileJson, Table, Upload, UserCheck } from 'lucide-react';
import api from '../api/client';
import './ChampionshipTabs.css';

interface ChampionshipEntryListTabProps {
  championship: Championship;
  onChange: (updates: Partial<Championship>) => void;
  cars: Car[];
}

interface EntryListFile {
  steamid_whitelist: string[];
  entrylist: Array<{
    steamid: string;
    ballast: number;
    restrictor: number;
    driverName?: string;
    car?: string;
    carName?: string;
    carNumber?: string;
    position?: number;
  }>;
}

interface ValidationErrors {
  [index: number]: {
    steamId?: string;
    carNumber?: string;
    ballast?: string;
    restrictor?: string;
  };
}

// Restrictor is stored as 0.00–0.20 in the JSON but displayed as 0–20 in the UI
const restrictorToDisplay = (v: number | undefined) => Math.round((v ?? 0) * 100);
const restrictorToStore = (display: number) => parseFloat((display / 100).toFixed(2));

const ChampionshipEntryListTab: React.FC<ChampionshipEntryListTabProps> = ({
  championship,
  onChange,
  cars,
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  const [editedRegistrations, setEditedRegistrations] = useState<ChampionshipRegistration[]>(
    () => championship.registrations || []
  );
  // Raw entrylist.json content for the JSON view
  const [entryListJson, setEntryListJson] = useState<EntryListFile | null>(null);
  const [jsonText, setJsonText] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch the actual entrylist.json when switching to JSON view
  useEffect(() => {
    if (viewMode === 'json' && championship.id) {
      api.getChampionshipEntryList(championship.id)
        .then((res) => {
          const data: EntryListFile | null = res.data.entrylist;
          setEntryListJson(data);
          setJsonText(data ? JSON.stringify(data, null, 2) : '{}');
          setJsonError(null);
        })
        .catch(() => {
          setJsonText('{}');
          setJsonError('Could not load entrylist.json from server.');
        });
    }
  }, [viewMode, championship.id]);

  useEffect(() => {
    setEditedRegistrations(championship.registrations || []);
  }, [championship.registrations]);

  const validate = (regs: ChampionshipRegistration[]): ValidationErrors => {
    const errors: ValidationErrors = {};
    const seenSteamIds = new Map<string, number>();
    const seenCarNumbers = new Map<string, number>();

    regs.forEach((reg, i) => {
      const e: ValidationErrors[number] = {};

      if (reg.steamId) {
        if (seenSteamIds.has(reg.steamId)) {
          e.steamId = 'Duplicate Steam ID';
          const prev = seenSteamIds.get(reg.steamId)!;
          errors[prev] = { ...errors[prev], steamId: 'Duplicate Steam ID' };
        } else {
          seenSteamIds.set(reg.steamId, i);
        }
      }

      if (reg.carNumber) {
        if (seenCarNumbers.has(reg.carNumber)) {
          e.carNumber = 'Duplicate car number';
          const prev = seenCarNumbers.get(reg.carNumber)!;
          errors[prev] = { ...errors[prev], carNumber: 'Duplicate car number' };
        } else {
          seenCarNumbers.set(reg.carNumber, i);
        }
      }

      const ballast = reg.ballast ?? 0;
      if (ballast < -100 || ballast > 100) e.ballast = '-100 to 100';

      // restrictor is stored as 0.00-0.20; display is 0-20
      const restrictorDisplay = restrictorToDisplay(reg.restrictor);
      if (restrictorDisplay < 0 || restrictorDisplay > 20) e.restrictor = '0 to 20';

      if (Object.keys(e).length > 0) errors[i] = e;
    });

    return errors;
  };

  const updateRegistration = (index: number, field: keyof ChampionshipRegistration, rawValue: any) => {
    const next = [...editedRegistrations];
    let value = rawValue;

    if (field === 'restrictor') {
      // UI shows 0-20, store as 0.00-0.20
      value = restrictorToStore(Number(rawValue));
    } else if (field === 'ballast') {
      value = Number(rawValue);
    }

    next[index] = { ...next[index], [field]: value };
    setEditedRegistrations(next);
    setValidationErrors(validate(next));
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleSaveFromTable = async () => {
    const errors = validate(editedRegistrations);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaveStatus('saving');
    setSaveError(null);
    try {
      const res = await api.saveChampionshipEntryList(championship.id, {
        registrations: editedRegistrations,
      });
      onChange({ registrations: res.data.championship?.registrations ?? editedRegistrations });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err: any) {
      setSaveStatus('error');
      setSaveError(err?.response?.data?.error || 'Failed to save entry list');
    }
  };

  const handleSaveFromJson = async () => {
    setJsonError(null);
    let parsed: EntryListFile;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setJsonError('Invalid JSON');
      return;
    }

    setSaveStatus('saving');
    setSaveError(null);
    try {
      const res = await api.saveChampionshipEntryList(championship.id, { entrylist: parsed });
      onChange({ registrations: res.data.championship?.registrations ?? editedRegistrations });
      setEntryListJson(parsed);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err: any) {
      setSaveStatus('error');
      setSaveError(err?.response?.data?.error || 'Failed to save entry list');
    }
  };

  const applyUploadedFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed: EntryListFile = JSON.parse(e.target?.result as string);
        setJsonText(JSON.stringify(parsed, null, 2));
        setEntryListJson(parsed);
        setViewMode('json');
        setJsonError(null);
      } catch {
        setJsonError('Could not parse uploaded file as JSON');
      }
    };
    reader.readAsText(file);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) applyUploadedFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) applyUploadedFile(file);
  };

  const getAllowedCarNames = (): Set<string> => {
    const s = new Set<string>();
    championship.events?.forEach((ev) => ev.allowed_cars_list_full?.forEach((c) => s.add(c.car_name)));
    return s;
  };

  const groupedCars = (): { allowed: Car[]; others: Car[] } => {
    const allowed_names = getAllowedCarNames();
    const allowed: Car[] = [], others: Car[] = [];
    cars.forEach((c) => (allowed_names.has(c.name) ? allowed : others).push(c));
    return { allowed, others };
  };

  const cellClass = (err?: string) => `table-input${err ? ' cell-error' : ''}`;

  return (
    <div
      className={`championship-tab-content championship-entrylist-tab${isDragOver ? ' drag-over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="entrylist-header">
        <h3>Entry List Management</h3>
        <div className="entrylist-actions">
          <div className="view-toggle">
            <button className={`toggle-button ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
              <Table size={16} /> Table View
            </button>
            <button className={`toggle-button ${viewMode === 'json' ? 'active' : ''}`} onClick={() => setViewMode('json')}>
              <FileJson size={16} /> entrylist.json
            </button>
          </div>
          <button
            className="toggle-button"
            onClick={() => fileInputRef.current?.click()}
            title="Upload entrylist.json"
          >
            <Upload size={16} /> Upload
          </button>
          <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFileInput} />
          <button
            className="save-entrylist-button"
            onClick={viewMode === 'table' ? handleSaveFromTable : handleSaveFromJson}
            disabled={saveStatus === 'saving' || (viewMode === 'table' && hasErrors)}
            title={viewMode === 'table' && hasErrors ? 'Fix validation errors before saving' : ''}
          >
            {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved!' : 'Save Entry List'}
          </button>
        </div>
      </div>

      {isDragOver && (
        <div className="drop-overlay">Drop entrylist.json here</div>
      )}

      {saveStatus === 'error' && saveError && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#f87171', fontSize: '13px' }}>
          {saveError}
        </div>
      )}

      <div className="entrylist-info">
        <p>
          {editedRegistrations.length} of {championship.spots || '?'} spots filled.
          {' '}Drag &amp; drop or upload an <code>entrylist.json</code> to import directly.
        </p>
      </div>

      {viewMode === 'table' ? (
        <div className="entrylist-table-container">
          <table className="entrylist-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Driver Name</th>
                <th>Steam ID</th>
                <th>Car</th>
                <th>Car #</th>
                <th>Ballast (kg)</th>
                <th>Restrictor (%)</th>
              </tr>
            </thead>
            <tbody>
              {editedRegistrations.map((reg, index) => {
                const err = validationErrors[index] || {};
                return (
                  <tr key={reg.id || index} className="registered-driver">
                    <td className="position-cell" data-label="#">
                      {index + 1}
                      <UserCheck size={14} style={{ marginLeft: '4px', color: '#22c55e', display: 'inline' }} />
                    </td>
                    <td data-label="Driver">
                      <input
                        type="text"
                        value={reg.driverName || ''}
                        onChange={(e) => updateRegistration(index, 'driverName', e.target.value)}
                        className="table-input"
                      />
                    </td>
                    <td data-label="Steam ID" title={err.steamId}>
                      <input
                        type="text"
                        value={reg.steamId || ''}
                        onChange={(e) => updateRegistration(index, 'steamId', e.target.value)}
                        className={cellClass(err.steamId)}
                      />
                    </td>
                    <td data-label="Car">
                      <select
                        value={reg.carName || ''}
                        onChange={(e) => updateRegistration(index, 'carName', e.target.value)}
                        className="table-select"
                      >
                        <option value="">—</option>
                        {(() => {
                          const { allowed, others } = groupedCars();
                          return (
                            <>
                              {allowed.length > 0 && (
                                <optgroup label="Championship Cars">
                                  {allowed.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
                                </optgroup>
                              )}
                              {others.length > 0 && (
                                <optgroup label="Other Cars (Admin Override)">
                                  {others.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
                                </optgroup>
                              )}
                            </>
                          );
                        })()}
                      </select>
                    </td>
                    <td data-label="Car #" title={err.carNumber}>
                      <input
                        type="text"
                        value={reg.carNumber || ''}
                        onChange={(e) => updateRegistration(index, 'carNumber', e.target.value)}
                        className={cellClass(err.carNumber) + ' small'}
                      />
                    </td>
                    <td data-label="Ballast (kg)" title={err.ballast}>
                      <input
                        type="number"
                        min="-100"
                        max="100"
                        value={reg.ballast ?? 0}
                        onChange={(e) => updateRegistration(index, 'ballast', e.target.value)}
                        className={cellClass(err.ballast) + ' small'}
                      />
                    </td>
                    <td data-label="Restrictor (%)" title={err.restrictor}>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        step="1"
                        value={restrictorToDisplay(reg.restrictor)}
                        onChange={(e) => updateRegistration(index, 'restrictor', e.target.value)}
                        className={cellClass(err.restrictor) + ' small'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {editedRegistrations.length === 0 && (
            <div className="empty-state">
              <p>No registered drivers yet. Upload an entrylist.json or wait for registrations.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="entrylist-json-container">
          {jsonError && (
            <div style={{ marginBottom: '8px', padding: '6px 10px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '4px', color: '#f87171', fontSize: '13px' }}>
              {jsonError}
            </div>
          )}
          <textarea
            className="json-editor"
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setJsonError(null); }}
            spellCheck={false}
          />
          <div className="json-format-info">
            <p>
              Editing the raw <code>entrylist.json</code> that the server reads. Saving will also back-populate the registrations list. You can also drag &amp; drop a file onto this panel.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChampionshipEntryListTab;
